import { HttpClient, HttpContext } from '@angular/common/http';
import { ChangeDetectorRef, Component, NgZone, OnInit } from '@angular/core';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { ToastrService } from 'ngx-toastr';
import { Subject } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { environment } from '../../../../environments/environment';
import { IS_UPLOAD_REQUEST } from 'src/app/types/types';

interface Msg {
  id: number;
  direction: string;        // 'in' | 'out'
  text?: string;
  messageType?: string;
  mediaId?: string;
  createdAt: string;
  audioUrl?: SafeUrl;
  loadingMedia?: boolean;
}

interface Conversation {
  number: string;
  name: string;
  messages: Msg[];
}

@Component({
  selector: 'app-bot-messages',
  templateUrl: './bot-messages.component.html',
  styleUrls: ['./bot-messages.component.scss']
})
export class BotMessagesComponent implements OnInit {

  loading = false;
  sending = false;
  recording = false;

  conversations: Conversation[] = [];
  selectedNumber: string | null = null;
  replyText = '';
  searchTerm = '';

  private search$ = new Subject<string>();
  private mediaRecorder: any;
  private chunks: any[] = [];

  private palette = ['#e5507e', '#3b7ddd', '#4bbf73', '#f0ad4e', '#8e5bd8', '#17a2b8'];

  constructor(private http: HttpClient, private toastr: ToastrService, private sanitizer: DomSanitizer,
    private zone: NgZone, private cdr: ChangeDetectorRef) { }

  ngOnInit(): void {
    // Debounce typing in the search box so we don't hit the server on every key.
    this.search$.pipe(debounceTime(350)).subscribe(() => {
      this.selectedNumber = null;
      this.load();
    });
    this.load();
  }

  onSearchChange(value: string): void {
    this.searchTerm = value || '';
    this.search$.next(this.searchTerm);
  }

  clearSearch(): void {
    if (!this.searchTerm) { return; }
    this.searchTerm = '';
    this.selectedNumber = null;
    this.load();
  }

  get isSearching(): boolean {
    return this.searchTerm.trim().length > 0;
  }

  load(): void {
    this.loading = true;
    const term = this.searchTerm.trim();
    const searchParam = term ? `&search=${encodeURIComponent(term)}` : '';
    // Pull a large batch and group into conversations client-side.
    this.http.get<any>(`${environment.serverUrl}/bot-message?skip=0&take=500${searchParam}`).subscribe({
      next: (res) => {
        this.conversations = this.buildConversations(res?.data || []);
        this.loading = false;
        // Keep selection if still present, else select the first.
        if (!this.selectedConversation && this.conversations.length > 0) {
          this.selectConversation(this.conversations[0]);
        } else if (this.selectedConversation) {
          this.loadConversationMedia(this.selectedConversation);
        }
      },
      error: (e) => {
        this.loading = false;
        console.error('Load bot messages failed', e);
        this.toastr.error(e?.error?.error || e?.message || 'Failed to load messages', 'Error');
      }
    });
  }

  private buildConversations(rows: any[]): Conversation[] {
    const map = new Map<string, Conversation>();
    for (const r of rows) {
      const key = r.contactNumber;
      if (!map.has(key)) { map.set(key, { number: key, name: '', messages: [] }); }
      const conv = map.get(key);
      conv.messages.push(r as Msg);
      if (!conv.name && r.profileName) { conv.name = r.profileName; }
    }
    const convs = Array.from(map.values());
    for (const c of convs) {
      c.messages.sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
    }
    convs.sort((a, b) => this.lastTime(b) - this.lastTime(a));
    return convs;
  }

  private lastTime(c: Conversation): number {
    const last = c.messages[c.messages.length - 1];
    return last ? new Date(last.createdAt).getTime() : 0;
  }

  get selectedConversation(): Conversation | undefined {
    return this.conversations.find(c => c.number === this.selectedNumber);
  }

  selectConversation(conv: Conversation): void {
    this.selectedNumber = conv.number;
    this.loadConversationMedia(conv);
  }

  lastMessage(c: Conversation): Msg | undefined {
    return c.messages[c.messages.length - 1];
  }

  preview(c: Conversation): string {
    const m = this.lastMessage(c);
    if (!m) { return ''; }
    if (this.isAudio(m)) { return '🎙 Voice message'; }
    if (m.mediaId) { return '📎 ' + (m.messageType || 'Attachment'); }
    return m.text || '';
  }

  isAudio(m: Msg): boolean {
    return m.messageType === 'audio' || m.messageType === 'voice';
  }

  hasMedia(m: Msg): boolean {
    return !!m.mediaId;
  }

  initials(c: Conversation): string {
    const src = (c.name || c.number || '').trim();
    const parts = src.split(/\s+/).filter(p => p.length);
    if (parts.length >= 2) { return (parts[0][0] + parts[1][0]).toUpperCase(); }
    return (src.substring(0, 2) || '?').toUpperCase();
  }

  color(c: Conversation): string {
    let hash = 0;
    const s = c.number || '';
    for (let i = 0; i < s.length; i++) { hash = s.charCodeAt(i) + ((hash << 5) - hash); }
    return this.palette[Math.abs(hash) % this.palette.length];
  }

  displayNumber(num: string): string {
    return num ? '+' + num : '';
  }

  private loadConversationMedia(conv: Conversation): void {
    for (const m of conv.messages) {
      if (this.isAudio(m) && !m.audioUrl && !m.loadingMedia) {
        this.fetchMedia(m);
      }
    }
  }

  private fetchMedia(m: Msg): void {
    m.loadingMedia = true;
    this.http.get(`${environment.serverUrl}/bot-message/${m.id}/media`, { responseType: 'blob' }).subscribe({
      next: (blob: Blob) => {
        m.loadingMedia = false;
        m.audioUrl = this.sanitizer.bypassSecurityTrustUrl(URL.createObjectURL(blob));
      },
      error: (e) => {
        m.loadingMedia = false;
        console.error('Fetch media failed', e);
      }
    });
  }

  openMedia(m: Msg): void {
    this.http.get(`${environment.serverUrl}/bot-message/${m.id}/media`, { responseType: 'blob' }).subscribe({
      next: (blob: Blob) => window.open(URL.createObjectURL(blob), '_blank'),
      error: (e) => { console.error('Open media failed', e); this.toastr.error('Could not fetch media', 'Error'); }
    });
  }

  sendReply(): void {
    const conv = this.selectedConversation;
    const text = this.replyText.trim();
    if (!conv || !text) { return; }

    this.sending = true;
    this.http.post<any>(`${environment.serverUrl}/bot-message/reply`,
      JSON.stringify({ to: conv.number, text })).subscribe({
      next: (res) => {
        this.sending = false;
        conv.messages.push({
          id: res?.id || Date.now(),
          direction: 'out',
          text,
          messageType: 'text',
          createdAt: new Date().toISOString()
        });
        this.replyText = '';
      },
      error: (e) => {
        this.sending = false;
        console.error('Send reply failed', e);
        this.toastr.error(e?.error?.error || e?.message || 'Failed to send (24h window may have closed)', 'Error');
      }
    });
  }

  // ---- Attachments ----

  onAttachSelected(event: any): void {
    const file = event?.target?.files?.[0];
    const conv = this.selectedConversation;
    if (!file || !conv) { return; }

    const fd = new FormData();
    fd.append('file', file);
    fd.append('to', conv.number);

    this.sending = true;
    this.cdr.detectChanges();
    this.http.post<any>(`${environment.serverUrl}/bot-message/reply-media`, fd, {
      context: new HttpContext().set(IS_UPLOAD_REQUEST, true)
    }).subscribe({
      next: (res) => {
        this.sending = false;
        this.appendOutbound(conv, res);
        event.target.value = '';
      },
      error: (e) => {
        this.sending = false;
        event.target.value = '';
        console.error('Send attachment failed', e);
        this.toastr.error(e?.error?.error || e?.message || 'Failed to send attachment', 'Error');
      }
    });
  }

  // ---- Voice note ----

  async toggleRecording(): Promise<void> {
    if (this.recording) { this.stopRecording(); return; }
    if (!this.selectedConversation) { return; }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      this.chunks = [];
      this.mediaRecorder = new (window as any).MediaRecorder(stream);
      this.mediaRecorder.ondataavailable = (e: any) => { if (e.data && e.data.size > 0) { this.chunks.push(e.data); } };
      this.mediaRecorder.onstop = () => {
        const blob = new Blob(this.chunks, { type: this.mediaRecorder.mimeType || 'audio/webm' });
        stream.getTracks().forEach((t: any) => t.stop());
        // onstop fires outside Angular's zone; run inside so the overlay updates.
        this.zone.run(() => this.uploadVoice(blob));
      };
      this.mediaRecorder.start();
      this.recording = true;
    } catch (e) {
      console.error('Microphone error', e);
      this.toastr.error('Could not access the microphone', 'Error');
    }
  }

  stopRecording(): void {
    if (this.mediaRecorder && this.recording) {
      this.recording = false;
      this.mediaRecorder.stop();
    }
  }

  private uploadVoice(blob: Blob): void {
    const conv = this.selectedConversation;
    if (!conv) { return; }
    const fd = new FormData();
    fd.append('file', blob, 'voice.webm');
    fd.append('to', conv.number);

    this.sending = true;
    this.cdr.detectChanges();
    this.http.post<any>(`${environment.serverUrl}/bot-message/reply-voice`, fd, {
      context: new HttpContext().set(IS_UPLOAD_REQUEST, true)
    }).subscribe({
      next: (res) => {
        this.sending = false;
        this.appendOutbound(conv, res);
      },
      error: (e) => {
        this.sending = false;
        console.error('Send voice failed', e);
        this.toastr.error(e?.error?.error || e?.message || 'Failed to send voice note', 'Error');
      }
    });
  }

  /** Append a just-sent outbound media message and load it for inline playback. */
  private appendOutbound(conv: Conversation, res: any): void {
    const msg: Msg = {
      id: res?.id || Date.now(),
      direction: 'out',
      messageType: res?.messageType,
      mediaId: res?.id ? 'sent' : undefined,
      createdAt: new Date().toISOString()
    };
    conv.messages.push(msg);
    if (this.isAudio(msg) && res?.id) { this.fetchMedia(msg); }
  }

  refresh(): void {
    this.load();
  }
}
